import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { Input } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";

import { accessControlForm } from "../../configs/Forms/AdvancedForms";
import {
  getAccessControl,
  insertAccessControl,
  updateAccessControl,
} from "./accessControlService";

const titleCase = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const AccessControlsAddEdit = () => {
  const { access_control_uuid } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(access_control_uuid);

  // MAIN STATE
  const [accessControl, setAccessControl] = useState(() => {
    if (editing) return {};

    // initialize Basic Info defaults
    const defaults = {};
    accessControlForm.sections["Basic Info"].forEach((field) => {
      defaults[field] = accessControlForm.defaults?.[field] ?? "";
    });
    return defaults;
  });

  const [nodes, setNodes] = useState([]);

  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState("");

  // Section → Setter map
  const setterMap = { Nodes: setNodes };
  const arrayMap = { Nodes: nodes };

  // FETCH EXISTING ACCESS CONTROL
  useEffect(() => {
    if (!editing) return;

    const fetchAccessControl = async () => {
      try {
        setLoading(true);
        const data = await getAccessControl(access_control_uuid);

        if (data.error) {
          setError(data.error);
          return;
        }

        setAccessControl({
          access_control_uuid: data.access_control_uuid,
          access_control_name: data.access_control_name,
          access_control_default: data.access_control_default,
          access_control_description: data.access_control_description || "",
        });

        setNodes(data.nodes || []);

      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAccessControl();
  }, [access_control_uuid, editing]);


  // ----------------------------
  // HELPERS
  // ----------------------------
  const handleChange = (field, value) =>
    setAccessControl((prev) => ({ ...prev, [field]: value }));

  const handleArrayChange = (setter, index, field, value, array) => {
    const updated = [...array];
    updated[index] = { ...updated[index], [field]: value };
    setter(updated);
  };

  const handleAddItem = (setter, sectionName, array) => {
    const template = {};
    accessControlForm.sections[sectionName].forEach((field) => {
      template[field] = accessControlForm.defaults?.[field] ?? "";
    });
    setter([...array, template]);
  };

  const handleDeleteItem = (section, index) => {
    const setter = setterMap[section];
    const array = arrayMap[section];
    setter(array.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setError("");
    const missing = [];

    // validate main required
    accessControlForm.required.forEach((field) => {
      if (!accessControl[field]) missing.push(field);
    });

    // validate Nodes required fields
    Object.entries(accessControlForm.requiredSections || {}).forEach(
      ([section, fields]) => {
        const items = { Nodes: nodes }[section] || [];
        items.forEach((item, idx) => {
          fields.forEach((f) => {
            if (!item[f]) missing.push(`${section}[${idx}].${f}`);
          });
        });
      }
    );

    if (missing.length) {
      setError(`Required fields missing: ${missing.join(", ")}`);
      return;
    }

    const user = JSON.parse(localStorage.getItem("user"));
    const user_uuid = user?.user_uuid || null;

    try {
      // Sanitize main object
      const sanitizedMain = {};
      Object.entries(accessControl).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined)
          sanitizedMain[key] = value;
      });

      // Sanitize Nodes array
      const sanitizeArray = (arr) =>
        arr
          .map((item) => {
            const sanitized = {};
            Object.entries(item).forEach(([key, value]) => {
              if (value !== "" && value !== null && value !== undefined)
                sanitized[key] = value;
            });
            return sanitized;
          })
          .filter((obj) => Object.keys(obj).length > 0);

      const payload = {
        accessControl: sanitizedMain,
        nodes: sanitizeArray(nodes),
        user_uuid: user_uuid
      };

      if (editing) {
        await updateAccessControl(payload.accessControl, payload.nodes, payload.user_uuid);
      } else {
        await insertAccessControl(payload.accessControl, payload.nodes, payload.user_uuid);
      }

      navigate("/admin/hucs/accessControls");
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to save");
    }
  };

  const renderField = (field, value, onChange) => {
    const type = accessControlForm.types[field];
    const options = accessControlForm.options?.[field] || [];
    const defaultValue = accessControlForm.defaults?.[field];
    const effectiveValue = value ?? defaultValue ?? "";

    const label =
      accessControlForm.labels?.[field] || titleCase(field);

    const placeholder = value
      ? ""
      : `Enter ${label.toLowerCase()}`;

    // BOOLEAN
    if (type === "boolean") {
      return (
        <Input
          type="select"
          value={effectiveValue}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </Input>
      );
    }

    // SELECT
    if (type === "select") {
      return (
        <Input
          type="select"
          value={effectiveValue}
          onChange={(e) => onChange(e.target.value)}
        >

          {options.map((opt) =>
            typeof opt === "object" ? (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ) : (
              <option key={opt} value={opt}>
                {opt}
              </option>
            )
          )}
        </Input>
      );
    }
    // DEFAULT (text / number)
    return (
      <Input
        type="text"
        value={effectiveValue}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  // ----------------------------
  // LOADING SCREEN
  // ----------------------------
  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading Access Control...</p>
      </div>
    );

  // ----------------------------
  // RENDER
  // ----------------------------
  return (

    <>
      <SimpleHeader
        name={`${editing ? "Edit" : "Add"} Access Control`}
        parentName="Access Controls"
      />

      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header className="header-theme">
            <h4 className="mb-0">
              {editing ? "EDIT ACCESS CONTROL" : "ADD ACCESS CONTROL"}
            </h4>
          </Card.Header>

          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}

            <Form>
              {Object.entries(accessControlForm.sections).map(
                ([sectionName, fields]) => {
                  const setter = setterMap[sectionName];
                  const items = arrayMap[sectionName];

                  return (
                    <div key={sectionName}>
                      <h5 className="section-title mb-3 mt-4" style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                        {sectionName}
                      </h5>

                      {/* ARRAY SECTIONS (Nodes) */}
                      {sectionName === "Nodes" &&
                        items?.map((item, idx) => (
                          <Row key={idx} className="mb-3">
                            {fields.map((field, i) => (
                              <Col md={3} key={i}>
                                <Form.Group className="mb-3">
                                  <Form.Label>
                                    {accessControlForm.labels[field] || field}
                                    {(accessControlForm.requiredSections?.[
                                      sectionName
                                    ] || []
                                    ).includes(field) && (
                                        <span style={{ color: "red" }}> *</span>
                                      )}
                                  </Form.Label>

                                  {renderField(
                                    field,
                                    item[field],
                                    value =>
                                      handleArrayChange(
                                        setter,
                                        idx,
                                        field,
                                        value,
                                        items
                                      )
                                  )}
                                </Form.Group>
                              </Col>
                            ))}

                            <Col md={2}>
                              <Button
                                variant="danger"
                                onClick={() =>
                                  handleDeleteItem(sectionName, idx)
                                }
                              >
                                Delete
                              </Button>
                            </Col>
                          </Row>
                        ))}

                      {sectionName === "Nodes" && (
                        <Button
                          className="mt-2"
                          onClick={() =>
                            handleAddItem(setter, sectionName, items)
                          }
                        >
                          Add Node
                        </Button>
                      )}

                      {/* BASIC INFO SECTION */}
                      {sectionName === "Basic Info" && (
                        <Row className="mb-2">
                          {fields.map((field, i) => (
                            <Col md={4} key={i}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  {accessControlForm.labels[field] || field}
                                  {accessControlForm.required.includes(
                                    field
                                  ) && <span style={{ color: "red" }}> *</span>}
                                </Form.Label>
                                {renderField(
                                  field,
                                  accessControl[field],
                                  value => handleChange(field, value)
                                )}
                              </Form.Group>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  );
                }
              )}

              <div className="mt-4 d-flex justify-content-between">
                <Button
                  variant="danger"
                  onClick={() => navigate("/admin/hucs/access-controls")}
                >
                  Cancel
                </Button>
                <Button variant="success" onClick={handleSave}>
                  Save
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default AccessControlsAddEdit;
